import { css } from '@emotion/react';
import { notNullsy } from '~/_shared/utils/typeGuards';
import {
  getReportTemplateDataRowDemographicId, type ReportDemographicData,
} from '~/exportReport/exportReportData.helpers';
import {
  ExportReportMapComponent, type ExportReportMapProps,
} from '~/exportReport/map/exportReportMap.component';
import { ExportReportNotesAndReferencesComponent } from '~/exportReport/notesAndReferences/exportReportNotesAndReferences.component';
import {
  ExportReportSummaryComponent, type ExportReportSummaryProps,
} from '~/exportReport/summary/exportReportSummary.component';
import { ExportReportTableComponent } from '~/exportReport/table/exportReportTable.component';
import { ExportReportTableDataRowComponent } from '~/exportReport/table/exportReportTableDataRow.component';
import { ExportReportTableEmptyRowComponent } from '~/exportReport/table/exportReportTableEmptyRow.component';
import { ExportReportTableHeaderComponent } from '~/exportReport/table/exportReportTableHeader.component';
import { ExportReportTableTitleRowComponent } from '~/exportReport/table/exportReportTableTitleRow.component';
import type { ReportTableColumn } from '~/exportReport/table/ReportTable.model';
import { noteDefinitions } from '~/exportReport/template/noteDefinitions';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { getDemographicTableCellValue } from '~/exportReport/templateRenderer/templateRenderer.helpers';

const tableStyle = css({
  marginTop: 10,
});

const mapsWrapperStyle = css({
  width: '100%',
  height: 300,
  breakInside: 'avoid',
  display: 'flex',
  gap: 10,
  justifyContent: 'space-between',
  marginTop: 10,
});

const tableTitleStyle = css({
  display: 'block',
  fontSize: 18,
  fontWeight: 700,
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const pageBreakStyle = css({
  breakAfter: 'page',
});

// TODO: remove after development of PDF Report is finished
const ADD_DEMOGRAPHIC_ID_IN_TITLE = false;

type ExportReportTemplateRendererProps = Readonly<{
  template: ReportTemplate;
  tableColumns: ReadonlyArray<ReportTableColumn>;
  summaryProps: ExportReportSummaryProps;
  onTableRowClick?: (itemIndex: number, rowIndex: number) => void;
  onMapLoaded?: () => void;
  mapProps: Pick<ExportReportMapProps, 'type' | 'center' | 'paths' | 'css' | 'radiusInMeters'>;
  getDemographicLabel: (demographicId: string) => string | null;
  getDemographicData: (columnId: string, demographicId: string) => ReportDemographicData | null;
  isPreviewMode?: boolean;
  isLoading?: boolean;
}>;

export const ExportReportTemplateRendererComponent = ({
  template, summaryProps, onTableRowClick, onMapLoaded, mapProps, tableColumns, getDemographicLabel, getDemographicData,
  isPreviewMode, isLoading,
}: ExportReportTemplateRendererProps) => {
  return (
    <>
      <ExportReportSummaryComponent {...summaryProps} />

      {template.items.map((templateItem, itemIndex) => {
        switch (templateItem.type) {
          case 'map': {
            return (
              <div key={`maps-${itemIndex}`} css={mapsWrapperStyle}>
                <ExportReportMapComponent
                  onMapLoaded={onMapLoaded}
                  id={`map${itemIndex}_roadmap`}
                  mapType="roadmap"
                  {...mapProps}
                />

                <ExportReportMapComponent
                  id={`map${itemIndex}_hybrid`}
                  mapType="hybrid"
                  {...mapProps}
                />
              </div>
            );
          }

          case 'page-break': {
            return (
              <div key={`page-break-${itemIndex}`} css={pageBreakStyle} />
            );
          }

          case 'line-break': {
            return (
              <div key={`line-break-${itemIndex}`}>
                <br />
              </div>
            );
          }

          case 'title': {
            return (
              <div key={`title-${itemIndex}`} css={tableTitleStyle}>{templateItem.content}</div>
            );
          }

          case 'table': {
            return (
              <ExportReportTableComponent key={`table-${itemIndex}`} css={tableStyle}>
                <ExportReportTableHeaderComponent
                  label={templateItem.title}
                  columnNames={tableColumns}
                />

                <tbody>
                  {templateItem.content.map((templateRow, rowIndex) => {
                    const handleTableRowClick = onTableRowClick
                      ? () => onTableRowClick(itemIndex, rowIndex)
                      : undefined;

                    if (templateRow.type === 'data-row') {
                      if (templateRow.hidden) {
                        return null;
                      }

                      const values = tableColumns.map(column => {
                        if (isPreviewMode) {
                          return {
                            value: templateRow.format_type ?? 'N/A',
                          };
                        }

                        return getDemographicTableCellValue({
                          columnId: column.id,
                          templateRow,
                          getDemographicData,
                        });
                      });

                      const demographicId = getReportTemplateDataRowDemographicId(templateRow);
                      const demographicLabel = getDemographicLabel(demographicId);
                      const isMissingDemographicLabel = !demographicLabel;
                      let label = templateRow.custom_label ?? demographicLabel ?? (isMissingDemographicLabel ? demographicId : '');
                      if (ADD_DEMOGRAPHIC_ID_IN_TITLE) {
                        label += ` | ${demographicId}`;
                      }

                      const isLabelError = !isLoading && isMissingDemographicLabel || values.some(value => value.isError);

                      return (
                        <ExportReportTableDataRowComponent
                          style={templateRow.style}
                          isInteractive={!!handleTableRowClick}
                          isLabelError={isLabelError}
                          key={`data-row${rowIndex}`}
                          label={label}
                          onClick={handleTableRowClick}
                          values={values}
                        />
                      );
                    }

                    if (templateRow.type === 'title-row') {
                      return (
                        <ExportReportTableTitleRowComponent
                          key={`title-row-${rowIndex}`}
                          isInteractive={!!handleTableRowClick}
                          onClick={handleTableRowClick}
                          style={templateRow.style}
                          title={templateRow.content}
                        />
                      );
                    }

                    if (templateRow.type === 'empty-row') {
                      return (
                        <ExportReportTableEmptyRowComponent
                          key={`empty-row-${rowIndex}`}
                          isInteractive={!!handleTableRowClick}
                          onClick={handleTableRowClick}
                        />
                      );
                    }

                    return null;
                  })}
                </tbody>
              </ExportReportTableComponent>
            );
          }

          case 'notes-and-references': {
            const notes = (templateItem.notes ?? [])
              .map(({ id }) => noteDefinitions.notes[id])
              .filter(notNullsy);

            const references = (templateItem.references ?? [])
              .map(({ id }) => noteDefinitions.references[id])
              .filter(notNullsy);

            return (
              <ExportReportNotesAndReferencesComponent
                key={`notes-and-references-${itemIndex}`}
                notes={notes}
                references={references}
              />
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
};
